import React from 'react';
import { groupBy, type Dictionary } from 'lodash';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { useSettings, Utils } from 'app/main/components';
import CompaniesFinancialStatementsTable, {
	type CompaniesFinancialStatementField
} from '../../components/CompaniesFinancialStatementsTable';
import CreditsafeLinkSection from '../../components/creditsafe/CreditsafeLinkSection';
import CreditsafeInfoSection from '../../components/creditsafe/CreditsafeInfoSection';
import CreditsafeReportFormatSection from '../../components/creditsafe/CreditsafeReportFormatSection';
import { showCreditsafeCompaniesSearchDialog } from '../../components/creditsafe/CreditsafeCompaniesSearchDialog';
import { showCreditsafeReportsDownloadDialog } from '../../components/creditsafe/CreditsafeReportsDownloadDialog';

interface CompaniesCreditsafeTabProps {
	compUid: string;
	creditsafeId: string;
	creditsafeUpdateDate: string;
	country: string;
	vatNumber: string;
	updateRefresh: VoidFunction;
}

const incomeStatementSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_totalValueOfProduction', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_operatingRevenues', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_changeInWorkInProgress', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_totalOtherIncomeAndRevenues', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalCostOfProduction', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_purchaseOfGoods', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_costOfServices', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_useOfThirdPartyAssets', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_totalPayrollAndRelatedCosts', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_changesInInventories', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_otherOperatingExpenses', style: 'currency', trend: 'negative' },
	{
		className: 'pl-16',
		name: 'csfs_totalAmortisationDepreciationAndWriteDowns',
		style: 'currency',
		trend: 'negative'
	},
	{ name: 'csfs_ebitda', style: 'currency', trend: 'positive' },
	{ name: 'csfs_ebit', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalFinancialIncomeAndExpense', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalValueAdjustmentsToFinancialAssetsAndLiabilities', style: 'currency', trend: 'positive' },
	{ name: 'csfs_preTaxResult', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalTaxesOnTheIncomeForTheYear', style: 'currency', trend: 'negative' },
	{ name: 'csfs_profitOrLossForTheYear', style: 'currency', trend: 'positive' },
	{ name: 'csfs_cashFlowPL', style: 'currency', trend: 'positive' }
];

const growthRatiosSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_changeInRevenuesPercentage', style: 'percent' },
	{ name: 'csfs_changeInTotalValueOfProductionPercentage', style: 'percent' },
	{ name: 'csfs_changeInTotalAssetsPercentage', style: 'percent' },
	{ name: 'csfs_changeInShareholdersEquityPercentage', style: 'percent' }
];

const profitabilityRatiosSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_returnOnSalesPercentage', style: 'percent' },
	{ name: 'csfs_returnOnInvestmentPercentage', style: 'percent' },
	{ name: 'csfs_returnOnEquityPercentage', style: 'percent' }
];

const liquidityRatiosSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_currentRatio' },
	{ name: 'csfs_acidTest' },
	{ name: 'csfs_debtorDays' },
	{ name: 'csfs_creditorDays' }
];

const balanceSheetAssetsSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_receivablesDueFromShareholders', style: 'currency', trend: 'positive' },
	{ name: 'csfs_intangibleFixedAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_tangibleFixedAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_financialFixedAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalFixedAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalInventories', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalReceivables', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_debtorsDueWithin1Year', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_dueFromSuppliersWithin1Year', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'csfs_prepaidTax', style: 'currency', trend: 'positive' },
	{ name: 'csfs_currentFinancialAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_liquidAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalCurrentAssets', style: 'currency', trend: 'positive' },
	{ name: 'csfs_accruedIncomeAndPrepayments', style: 'currency', trend: 'positive' },
	{ name: 'csfs_totalAssets', style: 'currency', trend: 'positive' }
];

const balanceSheetLiabilitiesSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'csfs_shareholdersEquity', style: 'currency', trend: 'positive' },
	{ name: 'csfs_shareCapital', style: 'currency', trend: 'positive' },
	{ name: 'csfs_otherReserves', style: 'currency', trend: 'positive' },
	{ name: 'csfs_netProfitOrLossForTheYear', style: 'currency', trend: 'positive' },
	{ name: 'csfs_provisionForRisksAndCharges', style: 'currency' },
	{ name: 'csfs_provisionForSeveranceIndemnity', style: 'currency' },
	{ name: 'csfs_totalPayables', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueWithin1Year', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueToSuppliersWithin1Year', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueToBankWithin1Year', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueAfter1Year', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueToSuppliersAfter1Year', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'csfs_dueToBankAfter1Year', style: 'currency', trend: 'negative' },
	{ name: 'csfs_otherPayables', style: 'currency', trend: 'negative' },
	{ name: 'csfs_accruedExpensesAndPrepayments', style: 'currency', trend: 'negative' },
	{ name: 'csfs_totalLiabilities', style: 'currency', trend: 'negative' }
];

export default function CompaniesCreditsafeTab(props: CompaniesCreditsafeTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);
	const canUpdate = authorizer.can('update', 'companies_financialstatements_creditsafe');

	const [isLoading, setIsLoading] = React.useState(true);
	const [creditsafeId, setCreditsafeId] = React.useState(props.creditsafeId);
	const [financialStatements, setFinancialStatements] = React.useState<Dictionary<any[]>>({});

	const [availableReportFormats, setAvailableReportFormats] = React.useState<string[]>([]);
	const [reportFormat, setReportFormat] = React.useState<string | undefined>();

	const currentFinancialStatements = reportFormat ? financialStatements[reportFormat] : [];

	function linkCreditsafeCompany() {
		if (!props.country || !props.vatNumber) {
			dispatch(Utils.alertLocalizedMsg(t, 'CREDITSAFE_DIALOG_SEARCH_ERROR', 'error'));
		} else {
			dispatch(
				showCreditsafeCompaniesSearchDialog({
					compUid: props.compUid,
					country: props.country,
					vatNumber: props.vatNumber,
					setCreditsafeId,
					updateRefresh: props.updateRefresh
				})
			);
		}
	}

	React.useEffect(() => {
		function downloadFinancialReports() {
			dispatch(
				showCreditsafeReportsDownloadDialog({
					compUid: props.compUid,
					creditsafeId: props.creditsafeId,
					updateRefresh: props.updateRefresh
				})
			);
		}

		document.addEventListener('onCompaniesCreditsafeReportsDownload', downloadFinancialReports);
		return () => document.removeEventListener('onCompaniesCreditsafeReportsDownload', downloadFinancialReports);
	}, [dispatch, props.compUid, props.creditsafeId, props.updateRefresh]);

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeStored({
			action: 'st_companies_financialstatements_creditsafe_list',
			dispatch,
			params: {
				n_page: 1,
				n_rows: 100,
				filters: [{ filt_field: 'csfs_comp_uid', filt_clause: 'equals', filt_value: props.compUid }]
			},
			successHandler: response => {
				const groupedReports = groupBy(response.data.list || [], 'csfs_accountFormat');
				const reportFormats = Object.keys(groupedReports);
				setFinancialStatements(groupedReports);
				setAvailableReportFormats(reportFormats);
				setReportFormat(reportFormats[0]);
				setIsLoading(false);
			}
		});
	}, [dispatch, props.compUid]);

	return (
		<Grid container spacing={2}>
			{canUpdate && (
				<Grid size={{ md: currentFinancialStatements.length > 0 ? 8 : 12, xs: 12 }}>
					{creditsafeId ? (
						<CreditsafeInfoSection
							creditsafeId={creditsafeId}
							lastImportDate={props.creditsafeUpdateDate}
							linkCreditsafeCompany={linkCreditsafeCompany}
							isLoading={isLoading}
						/>
					) : (
						<CreditsafeLinkSection linkCreditsafeCompany={linkCreditsafeCompany} isLoading={isLoading} />
					)}
				</Grid>
			)}
			{currentFinancialStatements.length > 0 && (
				<Grid size={{ md: 4, xs: 12 }}>
					<CreditsafeReportFormatSection
						availableReportFormats={availableReportFormats}
						reportFormat={reportFormat}
						setReportFormat={setReportFormat}
					/>
				</Grid>
			)}
			{currentFinancialStatements.length > 0 && (
				<>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_INCOME_STATEMENT')}
							fields={incomeStatementSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_GROWTH_RATIOS')}
							fields={growthRatiosSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_PROFITABILITY_RATIOS')}
							fields={profitabilityRatiosSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_LIQUIDITY_RATIOS')}
							fields={liquidityRatiosSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_BALANCE_SHEET_ASSETS')}
							fields={balanceSheetAssetsSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_CREDITSAFE_SECTION_BALANCE_SHEET_LIABILITIES')}
							fields={balanceSheetLiabilitiesSectionFields}
							financialStatements={currentFinancialStatements}
							dateField="csfs_yearEndDate"
						/>
					</Grid>
				</>
			)}
		</Grid>
	);
}
